import { logger } from '../lib/logger';
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, getCrmTenant } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Loader2, UserPlus, Crown, Calendar, Mail, Phone, Trash2, Pencil, Search,
  ShieldCheck, ExternalLink, AlertTriangle, KeyRound, Copy, CheckCircle2, UserCog, Link2,
  ArrowRightLeft,
} from 'lucide-react';
import { AgentRoleDialog, ROLE_LABELS } from '../components/agents/AgentRoleDialog';
import { TransferLeadsDialog } from '../components/agents/TransferLeadsDialog';
import { UserInviteResetCard } from '../components/agents/UserInviteResetCard';

const COUNTRIES = [
  { id: 'albania', label: '🇦🇱 Albania' },
  { id: 'indonesia', label: '🇮🇩 Indonesia' },
  { id: 'thailandia', label: '🇹🇭 Thailandia' },
];

const PATHS = [
  { id: 'equity_share', label: 'Equity Share' },
  { id: 'real_estate', label: 'Real Estate' },
  { id: 'both', label: 'Both (Equity + RE)' },
];

// ★ Fase 59 — ruoli operativi reali (allineati al backend / training multi-percorso)
const ROLES = [
  { id: 'ceo', label: '👑 CEO' },
  { id: 'co_ceo', label: '⭐ Co-CEO' },
  { id: 'senior_associate', label: 'Senior', labelKey: 'agentsPage.roleSenior' },
  { id: 'associate_manager', label: 'Associato', labelKey: 'agentsPage.roleAssociate' },
  { id: 'community', label: 'Community', labelKey: 'agentsPage.roleCommunity' },
];

const EMPTY = {
  name: '',
  email: '',
  phone: '',
  role: 'associate_manager',
  calendly_url: '',
  google_calendar_url: '',
  assignments: [],
  budget_threshold_eur: '',
  is_ceo_fallback: false,
  is_active: true,
  order: 100,
  notes_internal: '',
  send_invite: true, // ★ Fase 20 — invia subito email accessi (off = solo link)
};

export default function AgentsPage() {
  const { t } = useTranslation();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // null | 'new' | <agentDoc>
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [roleTarget, setRoleTarget] = useState(null);      // ★ Fase 59 — agent for role dialog
  const [transferTarget, setTransferTarget] = useState(null); // ★ Fase 38 — agent for transfer-leads dialog
  const [resetTarget, setResetTarget] = useState(null);    // agent for confirmation dialog
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null);    // { agent_name, temp_password, ... } shown ONCE
  const [inviteInfo, setInviteInfo] = useState(null);       // ★ Fase 20 — { name, email, link, emailed } dialog link primo accesso
  const [linkLoading, setLinkLoading] = useState(null);     // agent id in caricamento link
  const nav = useNavigate();
  // ★ 2026-09-04 (Fase 28) — Nel CRM IT il ruolo "Associato" non esiste più
  const isItTenant = getCrmTenant() === 'it';
  const roleOptions = ROLES.filter((r) => !(isItTenant && r.id === 'associate_manager'));

  const fetchAgents = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/agents');
      setAgents(data || []);
    } catch (e) {
      logger.error('AgentsPage: fetch failed', e);
      toast.error(t('agentsPage.loadError', 'Errore caricamento agenti'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAgents(); }, [fetchAgents]);

  const openNew = () => {
    // ★ Fase 28 — CRM IT: default Senior (il livello Associato non esiste più)
    setForm(isItTenant ? { ...EMPTY, role: 'senior_associate' } : EMPTY);
    setEditing('new');
  };
  const openEdit = (a) => {
    setForm({
      ...EMPTY,
      ...a,
      budget_threshold_eur: a.budget_threshold_eur ?? '',
      phone: a.phone ?? '',
      google_calendar_url: a.google_calendar_url ?? '',
      notes_internal: a.notes_internal ?? '',
    });
    setEditing(a);
  };
  const closeForm = () => { setEditing(null); setForm(EMPTY); };

  const toggleAssignment = (country, path) => {
    setForm((f) => {
      const exists = f.assignments.some((a) => a.country === country && a.path === path);
      const next = exists
        ? f.assignments.filter((a) => !(a.country === country && a.path === path))
        : [...f.assignments, { country, path }];
      return { ...f, assignments: next };
    });
  };

  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error(t('agentsPage.nameEmailRequired', 'Nome e email obbligatori'));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        budget_threshold_eur: form.budget_threshold_eur ? Number(form.budget_threshold_eur) : null,
        order: form.order ? Number(form.order) : 100,
      };
      if (editing === 'new') {
        const { data } = await api.post('/agents', {
          ...payload,
          // ★ Fase 20 — link primo accesso ancorato all'ambiente corrente
          invite_base_url: window.location.origin,
        });
        // ★ Fase 18/20 — invito automatico via email O solo generazione link
        if (data?.invite_status === 'sent') {
          toast.success(t('agentsPage.createdEmailSent', { defaultValue: 'Agente creato: accessi inviati via email a {{email}}', email: data.email }));
        } else if (data?.invite_status === 'link_only') {
          toast.success(t('agentsPage.createdLinkOnly', 'Agente creato: link di primo accesso generato (email NON inviata)'));
        } else if (data?.setup_link) {
          toast.warning(t('agentsPage.createdEmailFailed', 'Agente creato, ma email accessi non inviata. Copia il link manualmente.'));
        } else {
          toast.success(t('agentsPage.created', 'Agente creato'));
        }
        // Mostra sempre il dialog col link copiabile (se disponibile)
        if (data?.setup_link) {
          setInviteInfo({
            name: data.name,
            email: data.email,
            link: data.setup_link,
            emailed: data.invite_status === 'sent',
          });
        }
      } else {
        const { send_invite: _si, ...updatePayload } = payload;
        await api.put(`/agents/${editing.id}`, updatePayload);
        toast.success(t('agentsPage.updated', 'Agente aggiornato'));
      }
      closeForm();
      fetchAgents();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('agentsPage.saveError', 'Errore salvataggio'));
    } finally {
      setSaving(false);
    }
  };

  // ★ Fase 20 — genera il link di primo accesso per un agente esistente
  // (nessuna email inviata: da copiare e inviare manualmente)
  const generateInviteLink = async (agent) => {
    setLinkLoading(agent.id);
    try {
      const { data } = await api.post(`/agents/${agent.id}/invite-link`, {
        base_url: window.location.origin,
      });
      setInviteInfo({ name: data.name, email: data.email, link: data.link, emailed: false });
    } catch (e) {
      logger.error('AgentsPage: invite link failed', e);
      toast.error(e?.response?.data?.detail || t('agentsPage.linkError', 'Errore generazione link'));
    } finally {
      setLinkLoading(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/agents/${deleteTarget.id}`);
      toast.success(t('agentsPage.deletedOk', 'Agente eliminato'));
      setDeleteTarget(null);
      fetchAgents();
    } catch (e) {
      logger.error('AgentsPage: delete failed', e);
      toast.error(e?.response?.data?.detail || t('agentsPage.deleteError', 'Errore eliminazione'));
    }
  };

  const confirmResetPassword = async () => {
    if (!resetTarget) return;
    setResetting(true);
    try {
      const { data } = await api.post(`/agents/${resetTarget.id}/reset-password`);
      setResetTarget(null);
      setResetResult(data);
      fetchAgents();
    } catch (e) {
      logger.error('AgentsPage: reset password failed', e);
      toast.error(e?.response?.data?.detail || t('agentsPage.resetError', 'Errore reset password'));
    } finally {
      setResetting(false);
    }
  };

  const filtered = agents.filter((a) => {
    const q = search.toLowerCase();
    return !q || a.name.toLowerCase().includes(q) || a.email.toLowerCase().includes(q);
  });

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-5" data-testid="agents-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-serif text-2xl md:text-3xl text-[hsl(var(--primary))]">{t('agentsPage.title', 'Agenti commerciali')}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t('agentsPage.subtitle', { defaultValue: 'Gestione team vendite · routing automatico per (paese, percorso, budget) · {{count}} agenti totali.', count: agents.length })}
          </p>
        </div>
        <Button onClick={openNew} data-testid="agents-new-btn" className="bg-[var(--gp-gold)] text-[var(--gp-forest)] hover:bg-[var(--gp-gold-soft)]">
          <UserPlus className="w-4 h-4 mr-2" /> {t('agentsPage.newAgent', 'Nuovo agente')}
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <Search className="w-4 h-4 text-muted-foreground" />
        <Input
          placeholder={t('agentsPage.searchPlaceholder', 'Cerca per nome o email...')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
          data-testid="agents-search"
        />
      </div>

      {/* Inviti & Reset utenti CRM (per chi non è in agents, es. Eufemia admin) */}
      <UserInviteResetCard />

      {loading ? (
        <div className="py-20 grid place-items-center text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((a) => (
            <div
              key={a.id}
              className={`rounded-xl border p-4 bg-white shadow-sm relative cursor-pointer hover:shadow-md transition-shadow ${
                a.is_ceo_fallback ? 'border-[var(--gp-gold)] bg-gradient-to-br from-[var(--gp-gold)]/8 to-white' : 'border-border'
              } ${!a.is_active ? 'opacity-60' : ''}`}
              data-testid={`agent-card-${a.id}`}
              onClick={() => nav(`/app/agents/${a.id}`)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') nav(`/app/agents/${a.id}`); }}
            >
              <div className="flex items-start gap-3">
                <div className={`w-11 h-11 rounded-full flex items-center justify-center font-bold ${
                  a.is_ceo_fallback ? 'bg-[var(--gp-gold)] text-[var(--gp-forest)]' :
                  a.role === 'co_ceo' ? 'bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]' :
                  'bg-[var(--surface-2)] text-foreground'
                }`}>
                  {a.is_ceo_fallback ? <Crown className="w-5 h-5" /> : a.name.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-[hsl(var(--primary))] truncate">{a.name}</div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground" data-testid={`agent-role-label-${a.id}`}>
                    {ROLE_LABELS[a.role] || a.role || t('agentsPage.agent', 'Agente')}
                    {!a.is_active && ` · ${t('agentsPage.deactivated', 'DISATTIVATO')}`}
                  </div>
                </div>
                <div className="flex flex-col gap-1" onClick={(e) => e.stopPropagation()}>
                  <button onClick={(e) => { e.stopPropagation(); openEdit(a); }} className="p-1.5 hover:bg-[var(--surface-2)] rounded-md" data-testid={`agent-edit-${a.id}`} title={t('agentsPage.edit', 'Modifica')}>
                    <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); setRoleTarget(a); }} className="p-1.5 hover:bg-[var(--gp-gold)]/15 rounded-md" data-testid={`agent-role-${a.id}`} title={t('agentsPage.promoteDemote', 'Promuovi / Declassa')}>
                    <UserCog className="w-3.5 h-3.5 text-[var(--gp-forest)]" />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); setTransferTarget(a); }} className="p-1.5 hover:bg-emerald-50 rounded-md" data-testid={`agent-transfer-leads-${a.id}`} title={t('transferDialog.title', 'Trasferisci lead')}>
                    <ArrowRightLeft className="w-3.5 h-3.5 text-emerald-700" />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); setResetTarget(a); }} className="p-1.5 hover:bg-amber-50 rounded-md" data-testid={`agent-reset-pw-${a.id}`} title="Reset password">
                    <KeyRound className="w-3.5 h-3.5 text-amber-600" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); generateInviteLink(a); }}
                    className="p-1.5 hover:bg-sky-50 rounded-md"
                    data-testid={`agent-invite-link-${a.id}`}
                    title={t('agentsPage.generateLinkTitle', 'Genera link primo accesso (da inviare manualmente)')}
                    disabled={linkLoading === a.id}
                  >
                    {linkLoading === a.id
                      ? <Loader2 className="w-3.5 h-3.5 text-sky-600 animate-spin" />
                      : <Link2 className="w-3.5 h-3.5 text-sky-600" />}
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); setDeleteTarget(a); }} className="p-1.5 hover:bg-red-50 rounded-md" data-testid={`agent-delete-${a.id}`} title={t('agentsPage.delete', 'Elimina')}>
                    <Trash2 className="w-3.5 h-3.5 text-red-500" />
                  </button>
                </div>
              </div>

              <div className="mt-3 space-y-1.5 text-[12px] text-muted-foreground">
                <div className="flex items-center gap-1.5"><Mail className="w-3 h-3" /><span className="truncate">{a.email}</span></div>
                {a.phone && <div className="flex items-center gap-1.5"><Phone className="w-3 h-3" />{a.phone}</div>}
                {a.calendly_url ? (
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-3 h-3 text-[var(--gp-gold)]" />
                    <a
                      href={a.calendly_url}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-[var(--gp-gold)] hover:underline truncate inline-flex items-center gap-1"
                    >
                      Calendly <ExternalLink className="w-2.5 h-2.5" />
                    </a>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 text-yellow-700"><AlertTriangle className="w-3 h-3" />{t('agentsPage.calendlyMissing', 'Calendly mancante')}</div>
                )}
              </div>

              <div className="mt-3 pt-3 border-t border-border flex items-center justify-between text-[10px] text-[hsl(var(--primary))] font-medium">
                <span className="inline-flex items-center gap-1">
                  📊 {t('agentsPage.openDashboard', 'Apri dashboard agente')}
                </span>
                <span className="text-muted-foreground">→</span>
              </div>

              {a.budget_threshold_eur && (
                <div className="mt-2.5 text-[10px] uppercase tracking-wider text-[var(--gp-gold)] font-semibold">
                  💎 Trigger budget &gt; €{a.budget_threshold_eur.toLocaleString('it-IT')}
                </div>
              )}

              {a.assignments && a.assignments.length > 0 && (
                <div className="mt-2.5 flex flex-wrap gap-1">
                  {a.assignments.map((as, i) => (
                    <Badge key={`${as.country}-${as.path}-${i}`} variant="secondary" className="text-[10px] py-0.5 px-1.5">
                      {COUNTRIES.find((c) => c.id === as.country)?.label || as.country}
                      <span className="mx-1 opacity-50">·</span>
                      {as.path === 'both' ? 'Eq+RE' : as.path === 'equity_share' ? 'Equity' : 'RE'}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full text-center py-16 text-muted-foreground text-sm">{t('agentsPage.noAgentsFound', 'Nessun agente trovato.')}</div>
          )}
        </div>
      )}

      {/* Form Dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && closeForm()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="agent-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing === 'new' ? `➕ ${t('agentsPage.newAgent', 'Nuovo agente')}` : `✏️ ${t('agentsPage.edit', 'Modifica')} · ${editing?.name}`}</DialogTitle>
            <DialogDescription>
              {t('agentsPage.formDesc', 'Compila tutti i campi. Le assegnazioni (paese × percorso) determinano il routing automatico nel form qualifica.')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t('agentsPage.fullName', 'Nome completo')} *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Mario Rossi" data-testid="form-name" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="mario@hcf.com" data-testid="form-email" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t('agentsPage.phone', 'Telefono')}</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+39 333 1234567" />
              </div>
              <div>
                <Label>{t('agentsPage.role', 'Ruolo')}</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {roleOptions.map((r) => <SelectItem key={r.id} value={r.id}>{r.labelKey ? t(r.labelKey, r.label) : r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>{t('agentsPage.calendlyUrl', 'Calendly URL pubblico')}</Label>
              <Input value={form.calendly_url} onChange={(e) => setForm({ ...form, calendly_url: e.target.value })} placeholder="https://calendly.com/..." />
            </div>

            <div>
              <Label>Google Calendar URL <span className="text-[10px] text-muted-foreground">{t('agentsPage.gcalHint', '(per promemoria follow-up)')}</span></Label>
              <Input value={form.google_calendar_url} onChange={(e) => setForm({ ...form, google_calendar_url: e.target.value })} placeholder="https://calendar.app.google/..." />
            </div>

            <div>
              <Label>{t('agentsPage.assignments', 'Assegnazioni · (paese × percorso)')}</Label>
              <div className="grid grid-cols-4 gap-1 mt-2 text-xs">
                <div className="font-semibold text-muted-foreground">{t('agentsPage.country', 'Paese')}</div>
                {PATHS.map((p) => <div key={p.id} className="font-semibold text-muted-foreground text-center">{p.label}</div>)}
                {COUNTRIES.map((c) => (
                  <React.Fragment key={c.id}>
                    <div className="py-1.5">{c.label}</div>
                    {PATHS.map((p) => {
                      const checked = form.assignments.some((a) => a.country === c.id && a.path === p.id);
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => toggleAssignment(c.id, p.id)}
                          className={`py-1.5 px-2 rounded-md border transition-colors ${
                            checked ? 'bg-[var(--gp-gold)]/15 border-[var(--gp-gold)] text-[var(--gp-forest)] font-semibold' : 'border-border text-muted-foreground hover:bg-[var(--surface-2)]'
                          }`}
                          data-testid={`assign-${c.id}-${p.id}`}
                        >
                          {checked ? '✓' : '·'}
                        </button>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t('agentsPage.budgetThreshold', 'Soglia budget €')} <span className="text-[10px] text-muted-foreground">{t('agentsPage.optional', '(opzionale)')}</span></Label>
                <Input
                  type="number"
                  value={form.budget_threshold_eur}
                  onChange={(e) => setForm({ ...form, budget_threshold_eur: e.target.value })}
                  placeholder="250000"
                />
              </div>
              <div>
                <Label>{t('agentsPage.orderPriority', 'Ordine priorità')}</Label>
                <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} />
              </div>
            </div>

            <div className="space-y-2.5 pt-2">
              <div className="flex items-center justify-between gap-2">
                <Label className="cursor-pointer flex items-center gap-2">
                  <Crown className="w-4 h-4 text-[var(--gp-gold)]" /> {t('agentsPage.ceoFallback', 'CEO Fallback (cattura lead budget > soglia)')}
                </Label>
                <Switch checked={form.is_ceo_fallback} onCheckedChange={(v) => setForm({ ...form, is_ceo_fallback: v })} />
              </div>
              <div className="flex items-center justify-between gap-2">
                <Label className="cursor-pointer flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-emerald-600" /> {t('agentsPage.activeAgent', 'Agente attivo')}
                </Label>
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
              </div>
              {editing === 'new' && (
                <div className="flex items-center justify-between gap-2 rounded-lg border border-sky-200 bg-sky-50/60 px-3 py-2.5">
                  <div>
                    <Label className="cursor-pointer flex items-center gap-2 text-sky-900">
                      <Mail className="w-4 h-4 text-sky-600" /> {t('agentsPage.sendInvite', 'Invia subito email con gli accessi')}
                    </Label>
                    <p className="text-[11px] text-sky-800/70 mt-0.5">
                      {form.send_invite
                        ? t('agentsPage.sendInviteOn', 'L\u2019agente riceve subito l\u2019email con il link di primo accesso (valido 7 giorni).')
                        : t('agentsPage.sendInviteOff', 'Nessuna email: verr\u00e0 solo generato il link di prima registrazione, da copiare e inviare tu.')}
                    </p>
                  </div>
                  <Switch
                    checked={form.send_invite}
                    onCheckedChange={(v) => setForm({ ...form, send_invite: v })}
                    data-testid="form-send-invite"
                  />
                </div>
              )}
            </div>

            <div>
              <Label>{t('agentsPage.internalNotes', 'Note interne')}</Label>
              <textarea
                value={form.notes_internal}
                onChange={(e) => setForm({ ...form, notes_internal: e.target.value })}
                rows={2}
                placeholder={t('agentsPage.notesPlaceholder', 'Es: Specialista mercato Albania, parla albanese...')}
                className="w-full p-2 border rounded-md text-sm bg-background"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={closeForm}>{t('agentsPage.cancel', 'Annulla')}</Button>
            <Button onClick={save} disabled={saving} data-testid="form-save" className="bg-[var(--gp-gold)] text-[var(--gp-forest)] hover:bg-[var(--gp-gold-soft)]">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {editing === 'new' ? t('agentsPage.createAgent', 'Crea agente') : t('agentsPage.saveChanges', 'Salva modifiche')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" /> {t('agentsPage.deleteConfirmTitle', 'Elimina agente?')}
            </DialogTitle>
            <DialogDescription>
              {t('agentsPage.deleteConfirmDesc1', 'Stai eliminando')} <b>{deleteTarget?.name}</b>. {t('agentsPage.deleteConfirmDesc2', 'I lead già assegnati non saranno cancellati, ma i nuovi lead per le sue assegnazioni passeranno al CEO. Confermi?')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>{t('agentsPage.cancel', 'Annulla')}</Button>
            <Button variant="destructive" onClick={confirmDelete} data-testid="confirm-delete">
              {t('agentsPage.delete', 'Elimina')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ★ Fase 59 — Promozione / Declassamento con reset training automatico */}
      <AgentRoleDialog
        agent={roleTarget}
        open={!!roleTarget}
        onClose={() => setRoleTarget(null)}
        onChanged={fetchAgents}
      />

      {/* ★ Fase 38 — Trasferimento massivo lead ad altro agente */}
      <TransferLeadsDialog
        agent={transferTarget}
        agents={agents}
        onClose={() => setTransferTarget(null)}
        onDone={fetchAgents}
      />

      {/* Password reset confirmation */}
      <Dialog open={!!resetTarget} onOpenChange={(o) => !o && setResetTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <KeyRound className="w-5 h-5" /> {t('agentsPage.resetPwTitle', 'Resetta password')}
            </DialogTitle>
            <DialogDescription>
              {t('agentsPage.resetPwDesc1', 'Stai per generare una nuova password provvisoria per')}{' '}
              <b>{resetTarget?.name}</b>. {t('agentsPage.resetPwDesc2', 'La password attuale smetterà di funzionare immediatamente.')}
              <br /><br />
              {t('agentsPage.resetPwDesc3', 'La nuova password ti verrà mostrata UNA SOLA VOLTA in chiaro — copiala e inviala via Telegram all\u2019agente. Al primo login dovrà cambiarla.')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setResetTarget(null)} disabled={resetting}>
              {t('agentsPage.cancel', 'Annulla')}
            </Button>
            <Button
              onClick={confirmResetPassword}
              disabled={resetting}
              data-testid="confirm-reset-password"
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {resetting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {t('agentsPage.generateNewPw', 'Genera nuova password')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password reset RESULT — shown ONCE in plaintext */}
      <Dialog open={!!resetResult} onOpenChange={(o) => !o && setResetResult(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="w-5 h-5" /> {t('agentsPage.pwGenerated', 'Password generata')}
            </DialogTitle>
            <DialogDescription>
              {t('agentsPage.pwGeneratedDesc1', 'Nuova password provvisoria per')} <b>{resetResult?.agent_name}</b>.
              {' '}{t('agentsPage.pwGeneratedDesc2', 'Comunicala via Telegram (al primo accesso dovrà cambiarla).')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 my-4">
            <div className="p-3 rounded-md bg-[var(--surface-2)] border border-border">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Email</div>
              <div className="font-mono text-sm">{resetResult?.agent_email}</div>
            </div>
            <div className="p-3 rounded-md bg-amber-50 border border-amber-200">
              <div className="text-[10px] uppercase tracking-wider text-amber-800 mb-1 flex items-center justify-between">
                <span>{t('agentsPage.tempPassword', 'Password provvisoria')}</span>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(resetResult.temp_password);
                      toast.success(t('agentsPage.copiedClipboard', 'Copiata negli appunti'));
                    } catch (err) {
                      logger.error('Clipboard write failed', err);
                      toast.error(t('agentsPage.copyFailed', 'Impossibile copiare automaticamente'));
                    }
                  }}
                  className="inline-flex items-center gap-1 text-amber-700 hover:text-amber-900"
                  data-testid="copy-temp-password"
                >
                  <Copy className="w-3 h-3" /> {t('agentsPage.copy', 'Copia')}
                </button>
              </div>
              <div className="font-mono text-base font-semibold text-amber-900 select-all break-all">
                {resetResult?.temp_password}
              </div>
            </div>
            <div className="flex items-start gap-2 text-[11px] text-muted-foreground">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
              <span>
                {t('agentsPage.pwNotSaved', 'Questa password NON viene salvata in chiaro nel sistema. Una volta chiusa questa finestra non sarà più recuperabile — sarà necessario un nuovo reset.')}
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setResetResult(null)} data-testid="close-reset-result">
              {t('agentsPage.copiedClose', 'Ho copiato, chiudi')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ★ Fase 20 — Link primo accesso (copiabile, da inviare manualmente) */}
      <Dialog open={!!inviteInfo} onOpenChange={(o) => !o && setInviteInfo(null)}>
        <DialogContent className="max-w-lg" data-testid="invite-link-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sky-700">
              <Link2 className="w-5 h-5" /> {t('agentsPage.inviteLinkTitle', 'Link primo accesso')}
            </DialogTitle>
            <DialogDescription>
              {inviteInfo?.emailed ? (
                <>{t('agentsPage.inviteEmailSent1', 'Email con gli accessi già inviata a')} <b>{inviteInfo?.name}</b>. {t('agentsPage.inviteEmailSent2', 'Qui sotto trovi lo stesso link, se vuoi inoltrarlo anche manualmente.')}</>
              ) : (
                <>{t('agentsPage.inviteLinkOnly1', 'Link di prima registrazione per')} <b>{inviteInfo?.name}</b>. <b>{t('agentsPage.inviteLinkOnly2', 'Nessuna email è stata inviata')}</b>{t('agentsPage.inviteLinkOnly3', ': copialo e invialo tu (email, WhatsApp, Telegram...).')}</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 my-2">
            <div className="p-3 rounded-md bg-[var(--surface-2)] border border-border">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{t('agentsPage.agentEmail', 'Email agente')}</div>
              <div className="font-mono text-sm" data-testid="invite-link-email">{inviteInfo?.email}</div>
            </div>
            <div className="p-3 rounded-md bg-sky-50 border border-sky-200">
              <div className="text-[10px] uppercase tracking-wider text-sky-800 mb-1 flex items-center justify-between">
                <span>{t('agentsPage.inviteLinkValid', 'Link di primo accesso · valido 7 giorni')}</span>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(inviteInfo.link);
                      toast.success(t('agentsPage.linkCopied', 'Link copiato negli appunti'));
                    } catch (err) {
                      logger.error('Clipboard write failed', err);
                      toast.error(t('agentsPage.copyFailed', 'Impossibile copiare automaticamente'));
                    }
                  }}
                  className="inline-flex items-center gap-1 text-sky-700 hover:text-sky-900"
                  data-testid="copy-invite-link"
                >
                  <Copy className="w-3 h-3" /> {t('agentsPage.copy', 'Copia')}
                </button>
              </div>
              <div className="font-mono text-[11px] text-sky-900 select-all break-all" data-testid="invite-link-url">
                {inviteInfo?.link}
              </div>
            </div>
            <div className="flex items-start gap-2 text-[11px] text-muted-foreground">
              <AlertTriangle className="w-3.5 h-3.5 text-sky-500 mt-0.5 flex-shrink-0" />
              <span>
                {t('agentsPage.inviteLinkNote', 'Aprendo il link, l\u2019agente imposta la propria password. Generare un nuovo link non invalida la password attuale né i link già inviati.')}
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setInviteInfo(null)} data-testid="close-invite-link">
              {t('agentsPage.close', 'Chiudi')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
